/**
 * Minecraft 风格的弹窗工具（替换原生 alert/confirm）
 * 支持全局挂载，返回 Promise，方便 async/await
 */

let container = null

function ensureContainer() {
  if (!container) {
    container = document.createElement('div')
    container.id = 'mc-dialog-root'
    container.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 2000000;
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    `

    const backdrop = document.createElement('div')
    backdrop.className = 'mc-dialog-backdrop'
    container.appendChild(backdrop)

    document.body.appendChild(container)
  }
}

function createDialog({ title = '提示', message, confirmText = '确定', cancelText = '取消', showCancel = false }) {
  ensureContainer()
  const wrapper = document.createElement('div')
  wrapper.className = 'mc-dialog-wrapper'
  wrapper.style.cssText = `
    pointer-events: auto;
    opacity: 0;
    transform: scale(0.9);
    transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.18, 0.89, 0.32, 1.28);
  `
  const dialog = document.createElement('div')
  dialog.className = 'mc-dialog'
  dialog.innerHTML = `
    <div class="mc-dialog-header">${title}</div>
    <div class="mc-dialog-body">${message}</div>
    <div class="mc-dialog-actions">
      ${showCancel ? `<button class="mc-dialog-btn mc-dialog-btn-cancel">${cancelText}</button>` : ''}
      <button class="mc-dialog-btn mc-dialog-btn-confirm">${confirmText}</button>
    </div>
  `
  wrapper.appendChild(dialog)
  container.appendChild(wrapper)

  // 动画入场
  requestAnimationFrame(() => {
    wrapper.style.opacity = '1'
    wrapper.style.transform = 'scale(1)'
  })

  return new Promise((resolve) => {
    const close = (result) => {
      wrapper.style.opacity = '0'
      wrapper.style.transform = 'scale(0.9)'
      setTimeout(() => {
        wrapper.remove()
        const remainWrappers = container ? container.querySelectorAll('.mc-dialog-wrapper').length : 0
        if (container && remainWrappers === 0) {
          container.remove()
          container = null
        }
        resolve(result)
      }, 200)
    }

    dialog.querySelector('.mc-dialog-btn-confirm').addEventListener('click', () => close(true))
    if (showCancel) {
      dialog.querySelector('.mc-dialog-btn-cancel').addEventListener('click', () => close(false))
    }

    // ESC 键取消
    const onKeydown = (e) => {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', onKeydown)
        close(showCancel ? false : true)
      }
    }
    document.addEventListener('keydown', onKeydown)
  })
}

// 全局 API
export const mcAlert = (message, options = {}) => createDialog({ ...options, message, showCancel: false })
export const mcConfirm = (message, options = {}) => createDialog({ ...options, message, showCancel: true })
export const mcPrompt = (message, defaultValue = '', options = {}) => {
  return new Promise((resolve) => {
    ensureContainer()
    const wrapper = document.createElement('div')
    wrapper.className = 'mc-dialog-wrapper'
    wrapper.style.cssText = `
      pointer-events: auto;
      opacity: 0;
      transform: scale(0.9);
      transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.18, 0.89, 0.32, 1.28);
    `
    const dialog = document.createElement('div')
    dialog.className = 'mc-dialog'
    dialog.innerHTML = `
      <div class="mc-dialog-header">${options.title || '输入'}</div>
      <div class="mc-dialog-body">${message}</div>
      <div class="mc-dialog-input-wrapper">
        <input type="text" class="mc-dialog-input" value="${defaultValue}" placeholder="${options.placeholder || ''}" />
      </div>
      <div class="mc-dialog-actions">
        <button class="mc-dialog-btn mc-dialog-btn-cancel">${options.cancelText || '取消'}</button>
        <button class="mc-dialog-btn mc-dialog-btn-confirm">${options.confirmText || '确定'}</button>
      </div>
    `
    wrapper.appendChild(dialog)
    container.appendChild(wrapper)

    const input = dialog.querySelector('.mc-dialog-input')
    requestAnimationFrame(() => {
      wrapper.style.opacity = '1'
      wrapper.style.transform = 'scale(1)'
      input.focus()
      input.select()
    })

    const close = (result) => {
      wrapper.style.opacity = '0'
      wrapper.style.transform = 'scale(0.9)'
      setTimeout(() => {
        wrapper.remove()
        const remainWrappers = container ? container.querySelectorAll('.mc-dialog-wrapper').length : 0
        if (container && remainWrappers === 0) {
          container.remove()
          container = null
        }
        resolve(result)
      }, 200)
    }

    dialog.querySelector('.mc-dialog-btn-confirm').addEventListener('click', () => close(input.value))
    dialog.querySelector('.mc-dialog-btn-cancel').addEventListener('click', () => close(null))

    const onKeydown = (e) => {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', onKeydown)
        close(null)
      } else if (e.key === 'Enter') {
        document.removeEventListener('keydown', onKeydown)
        close(input.value)
      }
    }
    document.addEventListener('keydown', onKeydown)
  })
}

// 挂载到 window（方便直接替换）
if (typeof window !== 'undefined') {
  window.mcAlert = mcAlert
  window.mcConfirm = mcConfirm
  window.mcPrompt = mcPrompt
}

// CSS 注入
const css = `
#mc-dialog-root {
  font-family: inherit;
}
#mc-dialog-root .mc-dialog-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(2px);
}
#mc-dialog-root .mc-dialog-wrapper {
  position: relative;
  z-index: 1;
}
#mc-dialog-root .mc-dialog { 
  background: rgba(0, 0, 0, 0.94);
  border: 6px solid #5a5a5a;
  border-radius: 8px;
  padding: 0;
  width: min(520px, calc(100vw - 24px));
  max-width: calc(100vw - 24px);
  max-height: min(80vh, 720px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.9), inset -4px -4px 0 rgba(0,0,0,0.4), inset 4px 4px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(10px);
  position: relative;
}

@media (max-width: 420px) {
  #mc-dialog-root .mc-dialog {
    border-width: 4px;
    border-radius: 10px;
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
    max-height: 82vh;
  }
  #mc-dialog-root .mc-dialog-header {
    padding: 16px 16px 10px;
    font-size: 15px;
  }
  #mc-dialog-root .mc-dialog-body {
    padding: 0 16px 14px;
    font-size: 13px;
  }
  #mc-dialog-root .mc-dialog-actions {
    padding: 10px 16px 16px;
    justify-content: stretch;
  }
  #mc-dialog-root .mc-dialog-btn {
    flex: 1;
    padding: 12px 12px;
    font-size: 12px;
  }
  #mc-dialog-root .mc-dialog-input-wrapper {
    padding: 0 16px 14px;
  }
  #mc-dialog-root .mc-dialog-input {
    height: 42px;
    font-size: 16px;
  }
}
#mc-dialog-root .mc-dialog::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, #5ea818 0%, #9ae054 50%, #5ea818 100%);
  border-bottom: 2px solid #3c6b10;
}
#mc-dialog-root .mc-dialog::after {
  content: '';
  position: absolute;
  top: 6px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
}
#mc-dialog-root .mc-dialog-header {
  padding: 20px 24px 12px;
  font-size: 16px;
  font-weight: 900;
  color: #fff;
  text-shadow: 2px 2px 0 #000;
  letter-spacing: 1px;
}
#mc-dialog-root .mc-dialog-body {
  padding: 0 24px 20px;
  font-size: 14px;
  color: rgba(255,255,255,0.88);
  line-height: 1.5;
  white-space: pre-wrap;
}
#mc-dialog-root .mc-dialog-input-wrapper {
  padding: 0 24px 16px;
}
#mc-dialog-root .mc-dialog-input {
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 6px 10px;
  border: 3px solid #000;
  background: rgba(255, 255, 255, 0.95);
  color: #000;
  font-family: inherit;
  font-size: 13px;
  font-weight: 800;
  outline: none;
  box-shadow: inset -2px -2px 0 rgba(0,0,0,0.25), inset 2px 2px 0 rgba(255,255,255,0.65);
}
#mc-dialog-root .mc-dialog-input:focus {
  filter: brightness(1.02);
}
#mc-dialog-root .mc-dialog-actions {
  display: flex;
  gap: 10px;
  padding: 12px 24px 24px;
  justify-content: flex-end;
}
#mc-dialog-root .mc-dialog-btn {
  padding: 10px 18px;
  border: 3px solid #000;
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.08s ease, filter 0.12s ease, box-shadow 0.12s ease;
}
#mc-dialog-root .mc-dialog-btn-confirm {
  background: linear-gradient(180deg, #6fc21f 0%, #5ea818 55%, #4f8f14 100%);
  box-shadow: inset -3px -3px 0 rgba(44, 84, 10, 0.75), inset 3px 3px 0 rgba(178, 246, 112, 0.55);
  color: #fff;
}
#mc-dialog-root .mc-dialog-btn-cancel {
  background: #999;
  box-shadow: inset -3px -3px 0 rgba(0,0,0,0.35), inset 3px 3px 0 rgba(255,255,255,0.10);
  color: #000;
}
#mc-dialog-root .mc-dialog-btn:hover:not(:disabled) {
  filter: brightness(1.08);
}
#mc-dialog-root .mc-dialog-btn:active:not(:disabled) {
  transform: translateY(1px);
  filter: brightness(0.98);
}
`
const style = document.createElement('style')
style.textContent = css
document.head.appendChild(style)

export default { mcAlert, mcConfirm }
